import { portalHttp } from './portalHttp';

// ---- P1-B customer portal: API calls (shapes mirror the backend records) ----
export interface LoginResponse { token: string; expiresAt: string; contactName: string; customerName: string; institutionName: string; partyType?: 'CUSTOMER' | 'SUPPLIER'; }
export interface Me { contactName: string; email: string; customerName: string; institutionName: string; institutionCode: string; uploadsEnabled: boolean; }
export interface PortalPage<T> { rows: T[]; total: number; page: number; size: number; }
export interface InvoiceRow {
  id: string; number: string; issueDate: string; dueDate?: string | null; currency: string; total: number; paid: number;
  outstanding: number; status: string; payable: boolean;
}
export interface InvoiceLine { description: string; quantity: number; unitPrice: number; discount: number; tax: number; lineTotal: number; }
export interface InvoiceDetail {
  id: string; number: string; issueDate: string; dueDate?: string | null; currency: string; subtotal: number; tax: number; total: number;
  paid: number; credited: number; outstanding: number; status: string; payable: boolean; lines: InvoiceLine[];
}
export interface ReceiptRow { id: string; number: string; date: string; amount: number; method?: string | null; reference?: string | null; invoiceNumber?: string | null; }
export interface CreditNoteRow { id: string; number: string; date: string; invoiceNumber?: string | null; total: number; status: string; }
export interface StatementRow { date: string; documentType: string; reference: string; debit: number; credit: number; balance: number; }
export interface Statement { from: string; to: string; openingBalance: number; closingBalance: number; rows: StatementRow[]; }
export interface UploadRow { id: string; originalName: string; contentType: string; sizeBytes: number; note?: string | null; invoiceId?: string | null; status: string; uploadedAt: string; }

const pdf = { responseType: 'blob' as const };

export const PortalApi = {
  institutionName: async (code: string): Promise<{ name: string }> =>
    (await portalHttp.get(`/auth/institution/${encodeURIComponent(code)}`)).data,
  login: async (institutionCode: string, email: string, password: string): Promise<LoginResponse> =>
    (await portalHttp.post('/auth/login', { institutionCode, email, password })).data,
  acceptInvite: async (institutionCode: string, token: string, password: string): Promise<void> => {
    await portalHttp.post('/auth/accept-invite', { institutionCode, token, password });
  },
  resetPassword: async (institutionCode: string, token: string, password: string): Promise<void> => {
    await portalHttp.post('/auth/reset-password', { institutionCode, token, password });
  },
  forgotPassword: async (institutionCode: string, email: string): Promise<void> => {
    await portalHttp.post('/auth/forgot-password', { institutionCode, email });
  },
  logout: async (): Promise<void> => { await portalHttp.post('/auth/logout'); },
  me: async (): Promise<Me> => (await portalHttp.get('/me')).data,
  invoices: async (status: string, page: number, size: number): Promise<PortalPage<InvoiceRow>> =>
    (await portalHttp.get('/invoices', { params: { ...(status ? { status } : {}), page, size } })).data,
  invoice: async (id: string): Promise<InvoiceDetail> => (await portalHttp.get(`/invoices/${id}`)).data,
  invoicePdf: async (id: string): Promise<Blob> => (await portalHttp.get(`/invoices/${id}/pdf`, pdf)).data,
  pay: async (id: string): Promise<{ payUrl: string }> => (await portalHttp.post(`/invoices/${id}/pay`)).data,
  receipts: async (page: number, size: number): Promise<PortalPage<ReceiptRow>> =>
    (await portalHttp.get('/receipts', { params: { page, size } })).data,
  receiptPdf: async (id: string): Promise<Blob> => (await portalHttp.get(`/receipts/${id}/pdf`, pdf)).data,
  creditNotes: async (page: number, size: number): Promise<PortalPage<CreditNoteRow>> =>
    (await portalHttp.get('/credit-notes', { params: { page, size } })).data,
  creditNotePdf: async (id: string): Promise<Blob> => (await portalHttp.get(`/credit-notes/${id}/pdf`, pdf)).data,
  statement: async (from?: string, to?: string): Promise<Statement> =>
    (await portalHttp.get('/statement', { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } })).data,
  statementPdf: async (from?: string, to?: string): Promise<Blob> =>
    (await portalHttp.get('/statement/pdf', { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) }, ...pdf })).data,
  uploads: async (): Promise<UploadRow[]> => (await portalHttp.get('/uploads')).data,
  upload: async (file: File, note: string, invoiceId?: string): Promise<UploadRow> => {
    const fd = new FormData();
    fd.append('file', file);
    if (note) fd.append('note', note);
    if (invoiceId) fd.append('invoiceId', invoiceId);
    return (await portalHttp.post('/uploads', fd)).data;
  },
};
// ---- end P1-B ----
